import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/contact/ContactForm';
import ContactInfo from '../components/contact/ContactInfo';
import { Phone, Clock, MapPin, Award, Wrench, Droplet, CheckCircle, XCircle, DollarSign, Shield, AlertTriangle, Flame, Home, Users, Gauge, ShieldCheck, UserCheck, Thermometer, Star, ArrowRight } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';

const TanklessWaterHeaterPricing = () => {
  // BirdEye widget loading
  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  // SEO Meta Tags
  useEffect(() => {
    document.title = "Denver Tankless Water Heater Cost: $5k Replacement, $8k New Install | Colorado First Plumbing";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get exact, flat-rate pricing for tankless water heaters in Denver. $5,000 for Navien/Rinnai replacement & $8,000 for new installs. 360 Google Reviews. No hidden fees.');
    }
  }, []);

  // Enhanced Schema for the entire page with Product + FAQ integration
  const completePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://colorado-plumbing.com/tankless-water-heater-pricing#webpage",
        "url": "https://colorado-plumbing.com/tankless-water-heater-pricing",
        "name": "Denver Tankless Water Heater Cost: $5k Replacement, $8k New Install | Colorado First Plumbing",
        "description": "Get exact, flat-rate pricing for tankless water heaters in Denver. $5,000 for Navien/Rinnai replacement & $8,000 for new installs. 360 Google Reviews. No hidden fees.",
        "isPartOf": { "@id": "https://colorado-plumbing.com/#website" },
        "breadcrumb": { "@id": "https://colorado-plumbing.com/tankless-water-heater-pricing#breadcrumb" }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://colorado-plumbing.com/tankless-water-heater-pricing#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://colorado-plumbing.com/" },
          { "@type": "ListItem", "position": 2, "name": "Tankless Water Heater Pricing", "item": "https://colorado-plumbing.com/tankless-water-heater-pricing" }
        ]
      },
      {
        "@type": "Plumber",
        "@id": "https://colorado-plumbing.com/",
        "name": "Colorado First Plumbing and Sewer LLC",
        "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "5108 Sydney Ave",
          "addressLocality": "Littleton",
          "addressRegion": "CO",
          "postalCode": "80130",
          "addressCountry": "US"
        },
        "url": "https://colorado-plumbing.com/",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "360"
        },
        "areaServed": [
          { "@type": "City", "name": "Denver" },
          { "@type": "AdministrativeArea", "name": "Denver Metro" }
        ]
      },
      {
        "@type": "Product",
        "name": "Navien NPE-240A Condensing Tankless Water Heater Replacement - Fully Installed",
        "image": [
          "https://colorado-plumbing.com/page-specific-content/castle-rock-plumber/colorado-first-plumbing-tankless-water-heater-installed-new.jpg"
        ],
        "description": "Flat-rate $5,000 replacement of an existing Navien tankless water heater with a new NPE-240A. Includes removal, installation, gas/water connections, and recirculation pump.",
        "sku": "NAV-REPLACE-240A",
        "mpn": "NPE-240A",
        "brand": { "@type": "Brand", "name": "Navien" },
        "offers": {
          "@type": "Offer",
          "url": "https://colorado-plumbing.com/tankless-water-heater-pricing#navien-replacement",
          "priceCurrency": "USD", 
          "price": "5000",
          "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
        },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "mainEntity": {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What's included in the $5,000 Navien NPE-240A replacement?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Professional removal of old unit, installation of new NPE-240A, up to 8 feet of gas and water connections, flush valves for maintenance"
              }
            },
            {
              "@type": "Question",
              "name": "Does the NPE-240A include a recirculation pump?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, a built-in recirculation pump and buffer tank are standard for nearly-instant hot water at fixtures."
              }
            },
            {
              "@type": "Question",
              "name": "Are rebates available for Navien tankless units?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, Colorado Natural Gas offers a $300 rebate and the unit is eligible for a 30% federal tax credit, up to $600, for high efficiency."
              }
            },
            {
              "@type": "Question",
              "name": "What requirements apply for replacement pricing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Existing compatible tankless setup, code-compliant gas line and venting, and prior inspection are required. Otherwise, new installation pricing applies."
              }
            }
          ]
        }
      },
      {
        "@type": "Product",
        "name": "Navien NPE-240A Condensing Tankless Water Heater - New Installation",
        "image": [
          "https://colorado-plumbing.com/page-specific-content/littleton-plumber/Navien-240-expert-affordable-install.JPEG"
        ],
        "description": "Flat-rate $8,000 new Navien NPE-240A installation. Includes all gas line upgrades, complete PVC venting, water line modifications, permits, and expert setup.",
        "sku": "NAV-NEW-240A",
        "mpn": "NPE-240A",
        "brand": { "@type": "Brand", "name": "Navien" },
        "offers": {
          "@type": "Offer",
          "url": "https://colorado-plumbing.com/tankless-water-heater-pricing#navien-new-install",
          "priceCurrency": "USD", 
          "price": "8000",
          "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
        },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "mainEntity": {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Why does new Navien installation cost $8,000?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It includes upgrading to a 3/4\" gas line, complete new venting, water line modifications, permitting, and full professional commissioning for code compliance and proper operation."
              }
            },
            {
              "@type": "Question",
              "name": "What type of venting is used for Navien condensing tankless?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Schedule 40/80 PVC or CPVC piping is used, which is less expensive and more efficient than metal."
              }
            },
            {
              "@type": "Question",
              "name": "Are permits included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, all required permits and inspections are included and managed by Colorado First Plumbing."
              }
            }
          ]
        }
      },
      {
        "@type": "Product",
        "name": "Rinnai RE180iN Non-Condensing Tankless Water Heater Replacement - Fully Installed",
        "image": [
          "https://colorado-plumbing.com/colorado-plumbing-uploads/locations/westminster/best-rinnai-tankless-install-westminster.jpeg"
        ],
        "description": "Flat-rate $5,000 replacement for existing Rinnai tankless models. Includes removal, installation, Ubbink venting system, WiFi capability (on select models)",
        "sku": "RIN-REPLACE-180IN",
        "mpn": "RE180iN",
        "brand": { "@type": "Brand", "name": "Rinnai" },
        "offers": {
          "@type": "Offer",
          "url": "https://colorado-plumbing.com/tankless-water-heater-pricing#rinnai-replacement",
          "priceCurrency": "USD", 
          "price": "5000",
          "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
        },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "mainEntity": {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What venting does Rinnai RE180iN use?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It uses Ubbink concentric venting with an aluminum inner pipe and PVC outer shell."
              }
            },
            {
              "@type": "Question",
              "name": "Can I control my Rinnai tankless unit with an app?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, select Rinnai models are WiFi-capable and connect via the Rinnai Central app for temperature and maintenance alerts."
              }
            },
            {
              "@type": "Question",
              "name": "What's the difference between condensing and non-condensing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Non-condensing units have 82% efficiency and require metal venting; condensing units achieve 96% efficiency and can use PVC venting."
              }
            },
            {
              "@type": "Question",
              "name": "What requirements apply for replacement?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Existing setup must match new unit requirements, with proper venting and gas line installed and inspected. Otherwise, new install pricing applies."
              }
            }
          ]
        }
      },
      {
        "@type": "Product",
        "name": "Rinnai RE180iN Non-Condensing Tankless Water Heater - New Installation",
        "image": [
          "https://colorado-plumbing.com/colorado-plumbing-uploads/locations/denver/expert-tankless-rinnai-install-denver.JPEG"
        ],
        "description": "Flat-rate $8,000 for new Rinnai RE180iN install. Includes new 3/4\" gas line, Ubbink venting, copper/propress piping, roof/wall penetration, and full setup.",
        "sku": "RIN-NEW-180IN",
        "mpn": "RE180iN",
        "brand": { "@type": "Brand", "name": "Rinnai" },
        "offers": {
          "@type": "Offer",
          "url": "https://colorado-plumbing.com/tankless-water-heater-pricing#rinnai-new-install",
          "priceCurrency": "USD", 
          "price": "8000",
          "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31"
        },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "mainEntity": {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What gas line size is required for Rinnai tankless?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "New installs include a 3/4\" gas line, necessary for sufficient BTU delivery to the tankless unit."
              }
            },
            {
              "@type": "Question",
              "name": "How often should my Rinnai unit be serviced?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Annual maintenance and flushing is required in Colorado due to hard water"
              }
            },
            {
              "@type": "Question",
              "name": "What does Code 10 mean on Rinnai units?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It indicates an air supply/exhaust blockage or full condensate trap; professional service is recommended."
              }
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What's included in the $5,000 Navien replacement?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Colorado First Plumbing and Sewer LLC's $5,000 Navien replacement includes professional removal of your existing unit, installation of the new NPE-240A, gas and water connections up to 8 feet, flush valves for maintenance This applies when existing PVC venting is compatible."
            }
          },
          {
            "@type": "Question",
            "name": "Does the NPE-240A have a recirculation pump?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, the Navien NPE-240A includes a built-in recirculation pump and buffer tank. This significantly reduces wait time for hot water at fixtures, preventing \"cold water sandwiches\" and providing near-instant hot water when properly configured."
            }
          },
          {
            "@type": "Question",
            "name": "What rebates are available for Navien tankless?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Colorado Natural Gas offers a $300 rebate for the Navien NPE-240A due to its 0.96 UEF rating. Additionally, you may qualify for a 30% federal tax credit (up to $600) for this high-efficiency unit. Colorado First Plumbing helps identify all available savings."
            }
          },
          {
            "@type": "Question",
            "name": "Why does new installation cost $8,000?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The $8,000 includes upgrading your gas line from 1/2\" to 3/4\" for proper BTU delivery, installing complete PVC venting through the roof or wall, water line modifications, permits, and professional commissioning. Colorado First Plumbing ensures code compliance."
            }
          },
          {
            "@type": "Question",
            "name": "What venting is used for Navien condensing units?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Navien NPE-240A uses 2\" or 3\" Schedule 40/80 PVC/CPVC piping for venting. The lower exhaust temperatures of condensing units allow for these less expensive materials compared to stainless steel required for non-condensing units."
            }
          },
          {
            "@type": "Question",
            "name": "Are permits required for tankless installation?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, the City and County of Denver and most Colorado jurisdictions require permits for water heater installation. Colorado First Plumbing and Sewer LLC handles all necessary permitting and inspections as part of our service."
            }
          },
          {
            "@type": "Question",
            "name": "What venting does Rinnai RE180iN use?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Rinnai RE180iN uses Ubbink concentric venting with an aluminum inner pipe and white PVC outer shell for intake air. Colorado First Plumbing ensures proper venting installation for safe and efficient operation."
            }
          },
          {
            "@type": "Question",
            "name": "Can I control my Rinnai with an app?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, many Rinnai models are WiFi capable and connect via the Rinnai Central app. This allows remote temperature control, usage monitoring, and maintenance alerts from your smartphone."
            }
          },
          {
            "@type": "Question",
            "name": "What's the difference between condensing and non-condensing?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Non-condensing units like the RE180iN have 82% efficiency and require metal venting. Condensing units achieve 96% efficiency by capturing exhaust heat and can use less expensive PVC venting. Both provide endless hot water."
            }
          },
          {
            "@type": "Question",
            "name": "What gas line size does Rinnai require?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Most Rinnai tankless units, especially the RU199iN (199,000 BTUs) and RE180iN, require a 3/4\" gas line for proper fuel delivery. Colorado First Plumbing upgrades existing 1/2\" lines as part of the $8,000 installation."
            }
          },
          {
            "@type": "Question",
            "name": "How often should I maintain my Rinnai tankless?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Due to Colorado's hard water, annual maintenance is essential. This involves flushing and descaling the heat exchanger to prevent scale buildup."
            }
          },
          {
            "@type": "Question",
            "name": "What does a Rinnai Code 10 error mean?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Code 10 indicates an air supply/exhaust blockage or full condensate trap. This could be from a clogged condensate line, freezing, or debris in the vent. Colorado First Plumbing diagnoses and resolves these issues quickly."
            }
          },
          {
            "@type": "Question",
            "name": "How often should I flush my tankless unit in Colorado?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Colorado First Plumbing and Sewer LLC recommends annual flushing due to Colorado's hard water conditions. Areas with extremely hard water may benefit from bi-annual service."
            }
          },
          {
            "@type": "Question",
            "name": "What happens if I skip annual maintenance?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": ", reduce efficiency by 20-30%, cause hot water delivery issues, and lead to expensive heat exchanger replacement. The $225 annual service protects your $5,000-$8,000 investment."
            }
          },
          {
            "@type": "Question",
            "name": "Is your descaling solution safe?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, we use only food-grade descaling solutions that are safe for your family and the environment. These professional-grade solutions effectively remove mineral deposits without damaging internal components or leaving harmful residues."
            }
          },
          {
            "@type": "Question",
            "name": "How long does the flushing service take?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our comprehensive flushing service typically takes 1.5-2 hours, including setup, descaling circulation, thorough flushing, reconnection, and performance testing. We provide same-day service with minimal disruption to your routine."
            }
          }
        ]
      }
    ]
  };

  // Smooth scroll to contact form
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-light to-trust-light/20">
      <Header />
      <main>
        {/* Hero Section - AFFORDABLE MESSAGING */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Trust Signals Banner */}
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <span className="text-white font-bold">🔥 Endless Hot Water • 15-20 Year Lifespan • 96% Efficiency • 360 Google Reviews</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
                Denver Metro's ONLY Affordable Tankless Contractor with Exact Online Prices
                <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                  Colorado First Plumbing and Sewer LLC
                </span>
              </h1>
              <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
                How can others claim "affordable" without showing prices? We prove affordable means transparent.
                See every exact price for Navien & Rinnai tankless systems online before you call. No hidden fees, no salesmen.
              </p>
              
              {/* Clickable Price Preview Cards */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <a href="#navien-replacement" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Navien Replacement: $5,000</span>
                </a>
                <a href="#navien-new-install" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Navien New Install: $8,000</span>
                </a>
                <a href="#rinnai-replacement" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Rinnai Replacement: $5,000</span>
                </a>
                <a href="#rinnai-new-install" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all">
                  <span className="text-yellow-300 font-bold">Rinnai New Install: $8,000</span>
                </a>
              </div>
              
              {/* Primary CTA - 3 Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                
                <button
                  onClick={scrollToContact}
                  className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:shadow-xl hover:scale-[1.02] text-gray-900 px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  📝 Submit Form
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Authority Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Denver Metro Trusts Our Affordable Tankless Installation Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Highlands Ranch, Backcountry, Sterling Ranch, and surrounding areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">360</div>
                <div className="text-lg font-semibold text-white mb-2">Google Reviews</div>
                <p className="text-blue-100 text-sm">4.8★ rating</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Highlands Ranch, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 360 Google Reviews</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pre-Installation Educational Block */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How Much Does Professional Tankless Water Heater Installation Cost in Denver Metro?</h2>
                        
            <p className="text-sm text-blue-200 mt-4">No salesmen - just expert Master Plumber assessment for proper tankless sizing</p>
          </div>
        </section>

        {/* Master Plumber Attribution */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <img
                  src="/colorado-plumbing-uploads/brand/codee-christian-plumbing-truck.png"
                  alt="Codee Jessen - Master Plumber with Colorado First Plumbing service truck"
                  className="w-32 h-32 rounded-full object-cover bg-white/5 border-4 border-white/20 shadow-lg"
                />
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-1">Master Plumber - Tankless Specialist</h3>
                  <h4 className="text-xl font-semibold text-yellow-300 mb-2">Codee Jessen</h4>
                                    <div className="text-blue-100 mb-4">
                    <p>• Specialties: Tankless Systems, Gas Line Sizing, Code Compliance</p>
                    <p>• Experience: 18,000+ jobs completed, Factory-trained on Navien & Rinnai</p>
                    <p>• Commitment: Transparent pricing with no hidden fees or surprise charges</p>
                  </div>
                  <Link to="/team/codee-jessen" className="text-yellow-300 hover:text-yellow-200 font-semibold">
                    Learn more about our Master Plumber →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Navien Replacement Section */}
        <section id="navien-replacement" className="py-16 bg-gradient-to-bl from-green-800 to-green-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">How Much Does Affordable Navien NPE-240A Replacement Cost?</h2>
            <p className="text-green-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Colorado First Plumbing and Sewer LLC offers transparent $5,000 flat-rate pricing for Navien tankless replacement - the only Denver Metro contractor showing exact costs online with no hidden fees.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Flame size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Navien NPE-240A Condensing Replacement</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$5,000</div>
                        <p className="text-green-200 text-sm">Replace existing Navien with new NPE-240A</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">96% energy efficiency rating</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Built-in recirculation pump</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">PVC venting (lower cost than steel)</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Professional removal of old unit</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                              </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <div className="bg-yellow-500/20 border border-yellow-400/50 rounded-lg p-4 mt-4">
                        <h5 className="text-yellow-300 font-bold text-sm mb-2">⚠️ Replacement Pricing Requirements:</h5>
                        <div className="text-green-100 text-xs space-y-1">
                          <p>• Existing tankless unit setup must match new unit requirements</p>
                          <p>• Compatible gas line size, vent materials, and system type required</p>
                          <p>• Previous installation must have been inspected by building department</p>
                          <p>• Replacement pricing applies ONLY to similar existing tankless models</p>
                          <p>• If modifications are needed, new installation pricing applies</p>
                        </div>
                      </div>
                    </div>

                    {/* Navien Replacement Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What's included in the $5,000 Navien replacement?</h4>
                                              </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Does the NPE-240A have a recirculation pump?</h4>
                        <p className="text-green-100">Yes, the Navien NPE-240A includes a built-in recirculation pump and buffer tank. This significantly reduces wait time for hot water at fixtures, preventing "cold water sandwiches" and providing near-instant hot water when properly configured.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What rebates are available for Navien tankless?</h4>
                        <p className="text-green-100">Colorado Natural Gas offers a $300 rebate for the Navien NPE-240A due to its 0.96 UEF rating. Additionally, you may qualify for a 30% federal tax credit (up to $600) for this high-efficiency unit. Colorado First Plumbing helps identify all available savings.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/page-specific-content/castle-rock-plumber/colorado-first-plumbing-tankless-water-heater-installed-new.jpg"
                    alt="Expert Navien NPE-240A tankless water heater installation"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/longmont/tankless-install-navien-colorado-first-plumbing-brighton.JPEG"
                    alt="Professional Navien tankless installation in Brighton"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Navien New Install Section */}
        <section id="navien-new-install" className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">What is the Cost for New Navien NPE-240A Installation?</h2>
            <p className="text-blue-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Complete new Navien condensing tankless installation at $8,000 includes gas line upgrades, PVC venting, and professional setup - Colorado First Plumbing and Sewer LLC's transparent pricing with no surprises.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Images First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/page-specific-content/littleton-plumber/Navien-240-expert-affordable-install.JPEG"
                    alt="Navien NPE-240A tankless installation with expert gas line configuration"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/lakewood/tankless-water-heater-install-lakewood.JPEG"
                    alt="Professional tankless water heater piping in Lakewood"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Home size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">New Navien NPE-240A Installation</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$8,000</div>
                        <p className="text-blue-200 text-sm">Complete new condensing system setup</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">3/4" gas line upgrade included</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Complete PVC venting installation</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Water line modifications</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Code compliance and permits</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">System commissioning and testing</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <p className="text-blue-200 text-sm mt-3 italic">*Installation within 5 feet of existing water heater location</p>
                    </div>

                    {/* Navien New Install Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Why does new installation cost $8,000?</h4>
                        <p className="text-blue-100">The $8,000 includes upgrading your gas line from 1/2" to 3/4" for proper BTU delivery, installing complete PVC venting through the roof or wall, water line modifications, permits, and professional commissioning. Colorado First Plumbing ensures code compliance.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What venting is used for Navien condensing units?</h4>
                        <p className="text-blue-100">Navien NPE-240A uses 2" or 3" Schedule 40/80 PVC/CPVC piping for venting. The lower exhaust temperatures of condensing units allow for these less expensive materials compared to stainless steel required for non-condensing units.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Are permits required for tankless installation?</h4>
                        <p className="text-blue-100">Yes, the City and County of Denver and most Colorado jurisdictions require permits for water heater installation. Colorado First Plumbing and Sewer LLC handles all necessary permitting and inspections as part of our service.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Tankless Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Why Upgrade to Tankless?
              </h2>
              <p className="text-xl text-blue-100">
                Stop running out of hot water and start saving money with endless hot water technology
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Thermometer size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Endless Hot Water</h3>
                <p className="text-blue-100 text-sm mb-4">Never run out of hot water again. Multiple showers, laundry, and dishwasher all at once.</p>
                <div className="text-yellow-300 font-bold text-lg">∞ Hot Water</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Star size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">15-20 Year Lifespan</h3>
                <p className="text-blue-100 text-sm mb-4">Lasts 2x longer than traditional tank water heaters. Better long-term investment.</p>
                <div className="text-yellow-300 font-bold text-lg">vs. 8-12 Years</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <ArrowRight size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">30% More Efficient</h3>
                <p className="text-blue-100 text-sm mb-4">Only heats water when needed. No standby energy loss from maintaining tank temperature.</p>
                <div className="text-yellow-300 font-bold text-lg">Lower Bills</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-purple-800 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CheckCircle size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Space Saving</h3>
                <p className="text-blue-100 text-sm mb-4">Wall-mounted units free up valuable floor space in basements, closets, and utility rooms.</p>
                <div className="text-yellow-300 font-bold text-lg">90% Smaller</div>
              </div>
            </div>
          </div>
        </section>

        {/* Rinnai Replacement Section */}
        <section id="rinnai-replacement" className="py-16 bg-gradient-to-bl from-red-800 to-red-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">How Much Does Rinnai RE180iN Replacement Cost?</h2>
            <p className="text-red-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Professional Rinnai non-condensing tankless replacement at $5,000 flat-rate - Colorado First Plumbing and Sewer LLC provides transparent pricing with proven reliability.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Star size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">Rinnai RE180iN Non-Condensing Replacement</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$5,000</div>
                        <p className="text-red-200 text-sm">Replace existing Rinnai with new RE180iN</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Proven reliability and performance</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Easy maintenance and service</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Ubbink concentric venting</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                              </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">WiFi capable models available</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <div className="bg-yellow-500/20 border border-yellow-400/50 rounded-lg p-4 mt-4">
                        <h5 className="text-yellow-300 font-bold text-sm mb-2">⚠️ Replacement Pricing Requirements:</h5>
                        <div className="text-red-100 text-xs space-y-1">
                          <p>• Existing Rinnai unit setup must match new RE180iN requirements</p>
                          <p>• Compatible concentric venting and gas line size required</p>
                          <p>• Previous installation must have been inspected by building department</p>
                          <p>• Replacement pricing applies ONLY to similar existing Rinnai models</p>
                          <p>• If modifications are needed, new installation pricing applies</p>
                        </div>
                      </div>
                    </div>

                    {/* Rinnai Replacement Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What venting does Rinnai RE180iN use?</h4>
                        <p className="text-red-100">Rinnai RE180iN uses Ubbink concentric venting with an aluminum inner pipe and white PVC outer shell for intake air. Colorado First Plumbing ensures proper venting installation for safe and efficient operation.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">Can I control my Rinnai with an app?</h4>
                        <p className="text-red-100">Yes, many Rinnai models are WiFi capable and connect via the Rinnai Central app. This allows remote temperature control, usage monitoring, and maintenance alerts from your smartphone.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What's the difference between condensing and non-condensing?</h4>
                        <p className="text-red-100">Non-condensing units like the RE180iN have 82% efficiency and require metal venting. Condensing units achieve 96% efficiency by capturing exhaust heat and can use less expensive PVC venting. Both provide endless hot water.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-6">
                  <img
                    src="/colorado-plumbing-uploads/locations/denver/expert-tankless-rinnai-install-denver.JPEG"
                    alt="Denver Rinnai tankless installation at flat rate $5000"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/westminster/best-rinnai-tankless-install-westminster.jpeg"
                    alt="Top quality Rinnai tankless with affordable upfront pricing"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Rinnai New Install Section */}
        <section id="rinnai-new-install" className="py-16 bg-gradient-to-br from-orange-800 to-orange-900">
          <div className="container mx-auto px-4 mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">What is the Cost for New Rinnai RE180iN Installation?</h2>
            <p className="text-orange-200 text-lg text-center mt-4 max-w-3xl mx-auto">
              Complete new Rinnai non-condensing tankless installation at $8,000 includes gas line upgrades, concentric venting, and professional setup with Colorado First Plumbing's transparent pricing.
            </p>
          </div>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Images First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/colorado-plumbing-uploads/locations/highlands-ranch/prefab-tankless-highlands-ranch.jpeg"
                    alt="Rinnai tankless with prefab copper and ProPress gas piping"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/denver/tankless-rinnai-concentric-vent-denver.JPEG"
                    alt="Professional Rinnai roof vent termination cap installation"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                  />
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Wrench size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2">New Rinnai RE180iN Installation</h3>
                        <div className="text-4xl font-bold text-yellow-300 mb-2">$8,000</div>
                        <p className="text-orange-200 text-sm">Complete non-condensing system setup</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">3/4" gas line installation</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Ubbink concentric venting system</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">ProPress copper connections</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Professional roof penetration</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                        <span className="text-white text-sm">Complete testing and commissioning</span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 inline-block"
                        >
                          📝 Fill Out Form
                        </button>
                        
                      </div>
                      <p className="text-orange-200 text-sm mt-3 italic">*Installation within 5 feet of existing water heater location</p>
                    </div>

                    {/* Rinnai New Install Q&A */}
                    <div className="space-y-6">
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What gas line size does Rinnai require?</h4>
                        <p className="text-orange-100">Most Rinnai tankless units, especially the RU199iN (199,000 BTUs) and RE180iN, require a 3/4" gas line for proper fuel delivery. Colorado First Plumbing upgrades existing 1/2" lines as part of the $8,000 installation.</p>
                      </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">How often should I maintain my Rinnai tankless?</h4>
                                              </div>
                      <div className="border-t border-white/20 pt-6">
                        <h4 className="text-lg font-bold text-yellow-300 mb-3">What does a Rinnai Code 10 error mean?</h4>
                        <p className="text-orange-100">Code 10 indicates an air supply/exhaust blockage or full condensate trap. This could be from a clogged condensate line, freezing, or debris in the vent. Colorado First Plumbing diagnoses and resolves these issues quickly.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Flushing and Maintenance Section */}
        <section className="py-16 bg-gradient-to-bl from-teal-800 to-teal-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Professional Tankless Maintenance & Flushing
              </h2>
                          </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              {/* Why Maintenance Matters */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">Why Annual Flushing is Critical in Colorado</h3>
                <div className="space-y-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="flex items-center mb-2">
                      <AlertTriangle className="w-6 h-6 text-yellow-400 mr-3" />
                      <h4 className="font-bold text-white">Hard Water Challenge</h4>
                    </div>
                    <p className="text-teal-100 text-sm">Colorado's mineral-rich hard water causes calcium and lime scale buildup in tankless heat exchangers. Without annual maintenance, this buildup reduces efficiency by up to 30% and can cause permanent damage.</p>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="flex items-center mb-2">
                      <Shield className="w-6 h-6 text-green-400 mr-3" />
                                          </div>
                                      </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="flex items-center mb-2">
                      <Gauge className="w-6 h-6 text-blue-400 mr-3" />
                      <h4 className="font-bold text-white">Performance & Efficiency</h4>
                    </div>
                    <p className="text-teal-100 text-sm">Regular descaling maintains peak efficiency, ensuring endless hot water and lower energy costs. Clean heat exchangers operate at manufacturer specifications, delivering the performance you paid for.</p>
                  </div>
                </div>
              </div>

              {/* Service Details */}
              <div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Droplet size={40} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Professional Flushing Service</h3>
                    <div className="text-4xl font-bold text-yellow-300 mb-2">$225</div>
                    <p className="text-teal-200 text-sm">Annual maintenance with food-grade descaling</p>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center">
                      <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                      <span className="text-white text-sm">Complete system isolation and flushing</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                      <span className="text-white text-sm">Food-grade descaling solution (safe & effective)</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                      <span className="text-white text-sm">Filter cleaning and inspection</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                      <span className="text-white text-sm">Performance testing and calibration</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                      <span className="text-white text-sm">Comprehensive system inspection</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle size={16} className="text-yellow-300 mr-3 flex-shrink-0" />
                                          </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      
                      
                    </div>
                    <p className="text-teal-200 text-sm mt-3">*Annual service recommended for Colorado's hard water conditions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Process */}
            <div className="bg-gradient-to-r from-teal-600/20 to-teal-700/20 rounded-xl p-8 border border-teal-400/30">
              <h3 className="text-2xl font-bold text-center text-white mb-8">Our Professional Flushing Process</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-lg">1</div>
                  <h4 className="font-bold text-white mb-2">System Isolation</h4>
                  <p className="text-teal-100 text-sm">Safely disconnect and isolate your tankless unit from gas, water, and electrical connections.</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-lg">2</div>
                  <h4 className="font-bold text-white mb-2">Descaling Solution</h4>
                  <p className="text-teal-100 text-sm">Circulate food-grade descaling solution through the heat exchanger to dissolve mineral deposits.</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-lg">3</div>
                  <h4 className="font-bold text-white mb-2">Thorough Flushing</h4>
                  <p className="text-teal-100 text-sm">Complete system flush with clean water to remove all descaling solution and loosened deposits.</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-lg">4</div>
                  <h4 className="font-bold text-white mb-2">Test & Verify</h4>
                  <p className="text-teal-100 text-sm">Reconnect system, test operation, and verify optimal performance before completing service.</p>
                </div>
              </div>
            </div>

            {/* FAQ for Maintenance */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-center text-white mb-8">Maintenance Questions & Answers</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <h4 className="text-lg font-bold text-yellow-300 mb-3">How often should I flush my tankless unit in Colorado?</h4>
                                  </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <h4 className="text-lg font-bold text-yellow-300 mb-3">What happens if I skip annual maintenance?</h4>
                                  </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <h4 className="text-lg font-bold text-yellow-300 mb-3">Is your descaling solution safe?</h4>
                  <p className="text-teal-100">Yes, we use only food-grade descaling solutions that are safe for your family and the environment. These professional-grade solutions effectively remove mineral deposits without damaging internal components or leaving harmful residues.</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <h4 className="text-lg font-bold text-yellow-300 mb-3">How long does the flushing service take?</h4>
                  <p className="text-teal-100">Our comprehensive flushing service typically takes 1.5-2 hours, including setup, descaling circulation, thorough flushing, reconnection, and performance testing. We provide same-day service with minimal disruption to your routine.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Guarantees Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-800 to-blue-900">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">Guaranteed Peace of Mind</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 2: Licensed & Insured */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <Award className="w-10 h-10 text-yellow-400 mb-4" />
                <h4 className="text-lg font-bold text-white mb-2">Licensed, Bonded & Insured</h4>
                <p className="text-blue-100">We are a fully licensed, bonded, and insured plumbing contractor with Master Plumber ownership. Factory-trained on Navien and Rinnai systems.</p>
              </div>
              {/* Card 3: No Sales Pressure */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <UserCheck className="w-10 h-10 text-blue-400 mb-4" />
                <h4 className="text-lg font-bold text-white mb-2">No Salesmen, Ever</h4>
                <p className="text-blue-100">Our licensed plumbers provide expert advice, not high-pressure sales pitches. We focus on proper sizing and honest recommendations for your needs.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Competitor Pricing Reality Check */}
        <section className="py-16 bg-gradient-to-br from-red-800 to-red-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                What Big Box Plumbing Companies Actually Charge
              </h2>
              <p className="text-xl text-red-100">
                Here's why most contractors hide their pricing and send salesmen instead of showing transparent costs online
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Their Hidden Pricing */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <XCircle size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Big Box Companies Charge:</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-red-900/40 rounded-lg p-4 border border-red-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-red-200 font-semibold">New Tankless Installation:</span>
                      <span className="text-red-200 font-bold text-lg">$11,000 - $17,000</span>
                    </div>
                  </div>
                  
                  <div className="bg-red-900/40 rounded-lg p-4 border border-red-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-red-200 font-semibold">Tankless Replacement:</span>
                      <span className="text-red-200 font-bold text-lg">$6,500 - $10,000</span>
                    </div>
                  </div>
                  
                  <div className="bg-red-900/40 rounded-lg p-4 border border-red-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-red-200 font-semibold">Sales Process:</span>
                      <span className="text-red-200 font-bold text-sm">Salesmen disguised as "technicians"</span>
                    </div>
                  </div>
                  
                  <div className="bg-red-900/40 rounded-lg p-4 border border-red-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-red-200 font-semibold">Pricing Transparency:</span>
                      <span className="text-red-200 font-bold text-sm">Hidden until they arrive</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Our Transparent Pricing */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <CheckCircle size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Colorado First Charges:</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-green-900/40 rounded-lg p-4 border border-green-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-green-200 font-semibold">New Tankless Installation:</span>
                      <span className="text-green-200 font-bold text-lg">$8,000</span>
                    </div>
                    <p className="text-green-300 text-xs mt-1">Save $3,000 - $9,000</p>
                  </div>
                  
                  <div className="bg-green-900/40 rounded-lg p-4 border border-green-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-green-200 font-semibold">Tankless Replacement:</span>
                      <span className="text-green-200 font-bold text-lg">$5,000</span>
                    </div>
                    <p className="text-green-300 text-xs mt-1">Save $1,500 - $5,000</p>
                  </div>
                  
                  <div className="bg-green-900/40 rounded-lg p-4 border border-green-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-green-200 font-semibold">Sales Process:</span>
                      <span className="text-green-200 font-bold text-sm">Licensed plumbers only</span>
                    </div>
                  </div>
                  
                  <div className="bg-green-900/40 rounded-lg p-4 border border-green-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-green-200 font-semibold">Pricing Transparency:</span>
                      <span className="text-green-200 font-bold text-sm">Shown online right now</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Important Warning Section */}
            <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 rounded-xl p-8 border border-yellow-400/50 mb-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <AlertTriangle size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">⚠️ Important: Beware of Unrealistically Low Pricing</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-bold text-yellow-300 mb-4">Quality Installation Reality:</h4>
                  <div className="space-y-3 text-yellow-100">
                    <p>Professional tankless installation with quality materials, proper gas line sizing, and code-compliant venting is expensive. Our pricing reflects the true cost of expert installation.</p>
                                        <p>These units can last 15-20 years when properly installed and maintained. Don't compromise on installation quality to save a few hundred dollars.</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xl font-bold text-yellow-300 mb-4">Red Flags to Watch For:</h4>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <XCircle size={16} className="text-red-400 mr-3 flex-shrink-0 mt-1" />
                      <span className="text-yellow-100 text-sm">Pricing significantly below ours (likely cutting corners)</span>
                    </div>
                    <div className="flex items-start">
                      <XCircle size={16} className="text-red-400 mr-3 flex-shrink-0 mt-1" />
                      <span className="text-yellow-100 text-sm">No pricing shown online (sales pressure tactics)</span>
                    </div>
                    <div className="flex items-start">
                      <XCircle size={16} className="text-red-400 mr-3 flex-shrink-0 mt-1" />
                      <span className="text-yellow-100 text-sm">Unlicensed or uninsured contractors</span>
                    </div>
                    <div className="flex items-start">
                      <XCircle size={16} className="text-red-400 mr-3 flex-shrink-0 mt-1" />
                      <span className="text-yellow-100 text-sm">No established business or track record</span>
                    </div>
                    <div className="flex items-start">
                      <XCircle size={16} className="text-red-400 mr-3 flex-shrink-0 mt-1" />
                      <span className="text-yellow-100 text-sm">High-pressure sales tactics or limited-time offers</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <div className="bg-white/10 rounded-lg p-6">
                  <h4 className="text-xl font-bold text-white mb-3">💡 Our Recommendation</h4>
                                  </div>
              </div>
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Why We Show Our Prices</h3>
              <p className="text-red-100 text-lg mb-6">
                We're confident in our value, our quality, and our pricing. When you know what others charge, 
                you'll understand why we're Denver Metro's choice for transparent, professional tankless installation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                
                <a 
                  href="https://www.google.com/search?q=Colorado+First+Plumbing+and+Sewer+LLC&tbm=lcl" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-white/30 hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 border-2 border-white"
                >
                  ⭐ Read 360 Google Reviews
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section - Competitive Positioning */}
        <section className="py-20 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-white">
                How Can Others Claim "Affordable" Without Showing Prices?
              </h2>
              <p className="text-xl text-trust-light">
                We're the ONLY qualified, licensed, and heavily reviewed tankless contractor in Denver Metro proving affordable means transparent.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
              {/* What Others Do */}
              <div>
                <h4 className="text-xl font-bold mb-6 text-red-300 flex items-center">
                  <XCircle className="mr-3" size={24} />
                  What Other Contractors Do:
                </h4>
                <div className="space-y-4">
                  <div className="bg-red-900/30 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-200 mb-1">"We need to send someone out for an estimate"</p>
                    <p className="text-red-300 text-sm">Translation: We're sending a salesman to pressure you</p>
                  </div>
                  <div className="bg-red-900/30 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-200 mb-1">"Call for pricing" or "$1000 OFF Today Only!"</p>
                    <p className="text-red-300 text-sm">$1000 off what? They won't tell you the actual price</p>
                  </div>
                  <div className="bg-red-900/30 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-200 mb-1">Hidden pricing until arrival</p>
                    <p className="text-red-300 text-sm">You're trapped once they're at your door</p>
                  </div>
                </div>
              </div>

              {/* What We Do */}
              <div>
                <h4 className="text-xl font-bold mb-6 text-success-primary flex items-center">
                  <CheckCircle className="mr-3" size={24} />
                  What We Do Different:
                </h4>
                <div className="space-y-4">
                  <div className="bg-success-primary/20 border-l-4 border-success-primary p-4 rounded-lg">
                    <p className="font-semibold text-success-light mb-1">Actual prices online right now</p>
                    <p className="text-success-light/80 text-sm">$5,000 replacement, $8,000 new install for both Navien & Rinnai</p>
                  </div>
                  <div className="bg-success-primary/20 border-l-4 border-success-primary p-4 rounded-lg">
                    <p className="font-semibold text-success-light mb-1">No salesmen - just skilled plumbers</p>
                    <p className="text-success-light/80 text-sm">Our techs install systems, they don't sell you things you don't need</p>
                  </div>
                  <div className="bg-success-primary/20 border-l-4 border-success-primary p-4 rounded-lg">
                    <p className="font-semibold text-success-light mb-1">18,000+ jobs with 360 Google Reviews</p>
                    <p className="text-success-light/80 text-sm">Proven track record with transparent pricing on every job</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Final CTA */}
            <div className="text-center">
              <div className="bg-gradient-to-r from-action-primary to-action-urgent rounded-2xl p-8 shadow-2xl">
                <h4 className="text-xl md:text-2xl font-bold mb-4">
                  Why Call a Plumbing Company to Receive a Salesman?
                </h4>
                <p className="text-lg mb-6 text-action-warm/90">
                  Book with us and know exactly what the price is. No hassle pricing you can see right now.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  
                  <a 
                    href="https://www.google.com/search?q=Colorado+First+Plumbing+and+Sewer+LLC&tbm=lcl" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 hover:bg-white/30 hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 border-2 border-white w-full sm:w-auto"
                  >
                    ⭐ Read 360 Google Reviews
                  </a>
                </div>
                <p className="text-action-warm/80 mt-4 text-sm font-medium">
                  No other plumbing contractor in Denver Metro with this many verified reviews is willing to show you their pricing online
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact" className="relative py-24 bg-gradient-to-br from-trust-deep to-authority-navy overflow-hidden">
          {/* Modern Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-block px-4 py-2 mb-6 rounded-full bg-trust-light text-trust-deep font-semibold text-sm border border-trust-primary/20">
                🤝 Get In Touch
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-neutral-white">
                Ready for Endless Hot Water?
              </h3>
              <p className="text-xl text-trust-light/90">
                Contact Colorado First Plumbing for professional tankless installation, transparent pricing, and expert solutions. 
                We're here to help with your hot water needs.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
              <div className="col-span-1 lg:col-span-2 order-2 lg:order-1">
                <ContactInfo />
              </div>
              
              <div className="col-span-1 lg:col-span-3 order-1 lg:order-2">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
      
      {/* Schema Injection */}
      <SchemaInjector 
        schemas={[completePageSchema]}
        title="Denver Tankless Water Heater Cost: $5k Replacement, $8k New Install | Colorado First Plumbing"
        description="Get exact, flat-rate pricing for tankless water heaters in Denver. $5,000 for Navien/Rinnai replacement & $8,000 for new installs. 360 Google Reviews. No hidden fees."
        canonical="https://colorado-plumbing.com/tankless-water-heater-pricing"
        keywords="denver tankless water heater cost, navien pricing denver, rinnai installation cost, flat-rate tankless installation, upfront plumbing pricing colorado"
      />
    </div>
  );
};

export default TanklessWaterHeaterPricing;